import * as React from "react";

import { useAllDeviceTypesByOrg } from "@/shared/domain/deviceTypes/hooks";
import { Combobox, Field } from "@/shared/foundation/primitives";

import { deviceTypeChoices, NO_DEVICE_TYPE } from "./deviceTypeChoices";

type DeviceTypeChoice = ReturnType<typeof deviceTypeChoices>[number];

interface DeviceTypeSelectProps {
  orgId: string;
  /** The chosen type's `dt_` ID, or `null` for no type. */
  value: string | null;
  onChange: (deviceTypeId: string | null) => void;
  disabled?: boolean;
  /**
   * The visible label above the field. Leave it out and pass
   * `aria-labelledby` instead when the form renders its own label element.
   */
  label?: React.ReactNode;
  "aria-labelledby"?: string;
}

/**
 * Picks one of the org's device types, or None, narrowing the list as the
 * name is typed. Not for use inside a `Dialog`: its popup renders beneath the
 * dialog, so a dialog lists `deviceTypeChoices` as radio buttons instead.
 */
export function DeviceTypeSelect({
  orgId,
  value,
  onChange,
  disabled = false,
  label,
  "aria-labelledby": ariaLabelledBy,
}: DeviceTypeSelectProps) {
  const deviceTypesQuery = useAllDeviceTypesByOrg(orgId);

  const items = React.useMemo(
    () => deviceTypeChoices(deviceTypesQuery.data),
    [deviceTypesQuery.data],
  );
  const chosen =
    items.find((item) => item.value === (value ?? NO_DEVICE_TYPE)) ?? null;

  return (
    <Field.Root invalid={deviceTypesQuery.isError}>
      <Combobox<DeviceTypeChoice>
        items={items}
        value={chosen}
        onValueChange={(next) =>
          onChange(
            next === null || next.value === NO_DEVICE_TYPE ? null : next.value,
          )
        }
        isItemEqualToValue={(item, other) => item.value === other.value}
        label={label}
        size="md"
        disabled={disabled || deviceTypesQuery.isPending}
        autoHighlight
      >
        <Combobox.TriggerInput
          placeholder="Search device types"
          aria-label={
            label === undefined && !ariaLabelledBy ? "Type" : undefined
          }
          aria-labelledby={ariaLabelledBy}
        />
        <Combobox.Content>
          <Combobox.Empty />
          <Combobox.List>
            {(item: DeviceTypeChoice) => (
              <Combobox.Item key={item.value} value={item} />
            )}
          </Combobox.List>
        </Combobox.Content>
      </Combobox>
      <Field.Error match={deviceTypesQuery.isError}>
        Failed to load device types.
      </Field.Error>
    </Field.Root>
  );
}
